import { difference, isNumber, uniq } from 'lodash';
import {
  apiGetMaterials,
  apiGetMaterialTextures,
  apiGetOptimizeMaterialList,
  apiGetOptimizeTextureList,
  apiGetTextureList,
} from '../../../../helpers/api';
import { fetchAllPages } from '../../../../helpers/apiHelper';
import {
  deleteTexture,
  getMaterial,
  getMaterialTexture,
  getTexture,
  updateDbMaterial,
  updateDbMaterialTextures,
  updateDbTexture,
} from '../../../../helpers/idb';
import { dispatcher } from '../../../../helpers/projectHelper';
import { curatorDebuggerActions } from '../../../../redux/slicers/admin/curatorDebuggerSlicer';
import { setTotalMaterials } from '../../../../redux/slicers/admin/curatorLoadingProgressBarSlicer';
import {
  setExpiredTextureList,
  setMissingMaterialList,
  setMissingTextureList,
} from '../../../../redux/slicers/admin/curatorMissingDataSlicer';
import {
  setOpenProjectTexturesVersionWasUpdated,
  setProjectLoadedTexturesData,
} from '../../../../redux/slicers/admin/curatorSlicer';
import { getUnityItemLoader } from './loadUnityProps';
import { loadZipV1MaterialDetails } from './loadZipV1MaterialDetails';
import {
  paginatedProductActions,
  paginatedProductSelectors,
} from '../../../../redux/slicers/admin/paginatedProductSlicer';
import { getAppState } from '../../../../redux/store';
import { SPECULATION_STATUS } from '../../../../config/constant/unityConstants';
import { isRetailConfiguratorPage } from '../../../../components/admin/curator/ProductBuilderComponents/commonHelper';

export const loadUnityMaterialDetails = async ({ groupPropId, materialDetails, unityContext, unityLoadedSelector, version, zipEntries, v1ProjectJson, shouldUploadTextures, v1Data }) => {
  dispatcher(setTotalMaterials(materialDetails.length));

  if (version === 1) {
    materialDetails = await loadZipV1MaterialDetails({
      entries: zipEntries,
      materialDetails,
      projectJson: v1ProjectJson,
      shouldUploadTextures,
    });
  }

  await storeProjectMaterialDetailsInDb(materialDetails, version);

  const initMaterial = (material) => {

    // if useSpecularCalculation key is not present then we will set useSpecularCalculation as 1 
    let _material = ('useSpecularCalculation' in material) ? {...material} : {
      ...material,
      useSpecularCalculation: SPECULATION_STATUS.ON
    }

    // if useSpecularCalculation is 0 then  we will set useSpecularCalculation as 1
    _material = material.useSpecularCalculation === 0 ?  {
      ...material,
      useSpecularCalculation: SPECULATION_STATUS.ON
    }:{..._material}

    unityContext.projectModule.ApplyMaterialReceiver(_material);
  };
  const loadMaterialToUnity = getUnityItemLoader(initMaterial, unityLoadedSelector);

  const missingMaterials = [];
  const missingTextures = [];
  const unityDebugArray = await Promise.all(
    materialDetails.map(async (material) => {
      return parseMaterial({ material, loadMaterialToUnity, missingMaterials, missingTextures, groupPropId });
    })
  );

  dispatcher(setMissingMaterialList(missingMaterials));
  dispatcher(setMissingTextureList(missingTextures));

  console.log('UNITY_DEBUG ->> MATERIAL LIST', unityDebugArray);
  if (!window.unityDebug) {
    window.unityDebug = { props: [], materialDatas: [] };
  }
  window.unityDebug.materialDatas = unityDebugArray;
  console.log('UNITY_DEBUG ->> ALL', JSON.parse(JSON.stringify(window.unityDebug)));

  dispatcher(curatorDebuggerActions.setInitialMaterialDataList(unityDebugArray));
};

export const storeProjectMaterialDetailsInDb = async (materialData, version) => {
  const baseTextureIds = [];
  const materialTextureIds = [];
  let materialIds = [];
  const textureVersionMap = {};

  const params = new URLSearchParams(window.location.search);
  const isFromProductPage = params.get('isFromProductPage');
  const isFromProductPageValue = isFromProductPage === true || isFromProductPage === 'true';

  materialData.forEach((md) => {
    // assumption -> in one project same texture can be only in one version as multiple version are not supported
    const {
      AOId,
      diffuseId,
      metallicId,
      normalId,
      roughnessId,
      transparencyId,
      loadedMaterialId,
      materialTextureId,
      displacementId
    } = md;

    baseTextureIds.push(AOId, diffuseId, metallicId, normalId, roughnessId, transparencyId, displacementId);
    materialIds.push(loadedMaterialId);

    if (materialTextureId) {
      materialTextureIds.push(materialTextureId);
    }

    const {
      aoVersion,
      diffuseVersion,
      metallicVersion,
      normalVersion,
      roughnessVersion,
      transparencyVersion,
      displacementVersion
    } = md;
    textureVersionMap[AOId] = aoVersion;
    textureVersionMap[diffuseId] = diffuseVersion;
    textureVersionMap[metallicId] = metallicVersion;
    textureVersionMap[normalId] = normalVersion;
    textureVersionMap[roughnessId] = roughnessVersion;
    textureVersionMap[transparencyId] = transparencyVersion;
    textureVersionMap[displacementId] = displacementVersion;
  });

  // fetch textures
  const textureIds = uniq(
    baseTextureIds.filter(Boolean).filter((id) => {
      const isNumber = /^\d+$/.test(id);
      return id !== 'MISSING' && isNumber;
    })
  );

  if (textureIds.length > 0) {
    let query = `id=${textureIds.join(',')}&ignore_lf_default=true&per_page=20`;
    if (isFromProductPageValue) {
      query += `&is_from_product_page=${isFromProductPageValue}`;
    }
    const textures = isRetailConfiguratorPage()
      ? await fetchAllPages((page) => {
          return apiGetOptimizeTextureList({ query: `${query}&page=${page}` });
        })
      : await fetchAllPages((page) => {
          return apiGetTextureList({ query: `${query}&page=${page}` });
        });

    if (textureIds.length !== textures?.results?.length) {
      const fetchedIds = textures.results.map((t) => t.id);
      const missingIds = difference(textureIds, fetchedIds);
      for (const id of missingIds) {
        // remove missing ids from idb
        await deleteTexture(parseInt(id));
      }
    }

    // TODO: check if results count is correct
    // store textures data in index db
    const mismatchVersionList = [];
    await Promise.all(
      textures.results.map((texture) => {
        if (textureVersionMap[texture.id]) {
          const version = textureVersionMap[texture.id];
          if (texture.version !== version) {
            mismatchVersionList.push(texture);
          }
        }

        return updateDbTexture(texture);
      })
    );

    // deleting the texture from indexedDb if they are expired one
    // const expiredTextureList = textures?.results?.filter(a => a?.is_customer_product_expired);
    // console.log("expiredTextureList", textures?.results, expiredTextureList)
    // await Promise.all(expiredTextureList.map(async texture => await deleteTexture(parseInt(texture?.id))));
    // dispatcher(setExpiredTextureList(expiredTextureList));

    dispatcher(setOpenProjectTexturesVersionWasUpdated(mismatchVersionList));
    if (textures?.results?.length) {
      dispatcher(setProjectLoadedTexturesData(textures.results));
    }
  }

  // fetch materials
  const ids = materialIds.filter(Boolean).filter((id) => `${id}`.toUpperCase() !== 'MISSING');
  materialIds = uniq(ids);
  if (materialIds.length > 0) {
    const materialsQuery = materialIds.map((id) => `id=${id}`).join('&'); // for old materials we already fetched new ids in parse v1 function
    const materials = isRetailConfiguratorPage()
      ? await fetchAllPages((page) => {
          return apiGetOptimizeMaterialList(`${materialsQuery}&per_page=20&page=${page}`);
        })
      : await fetchAllPages((page) => {
          return apiGetMaterials(`${materialsQuery}&per_page=20&page=${page}`);
        });

    const materialsWithMaterialImage = materials.results.filter((m) => Boolean(m.material_image));

    // store materials data in index db
    await Promise.all(
      materialsWithMaterialImage.map((material) => {
        return updateDbMaterial(material);
      })
    );
  }

  // fetch material texture
  if (materialTextureIds.length > 0) {
    await Promise.all(
      materialTextureIds.map(async (materialTextureId) => {
        const materialsResponse = await apiGetMaterialTextures(
          `old_texture_id=${materialTextureId}`
        );
        const material = materialsResponse.results[0];

        let dbMaterial;
        try {
          dbMaterial = await updateDbMaterialTextures(material);
        } catch (e) {
          console.error(e);
          // TODO: handle errors if needed
        }
      })
    );
  }
};

export const parseMaterial = async ({ material, loadMaterialToUnity, missingMaterials, missingTextures, groupPropId }) => {
  // this should be always called after material/textures was added to idnexdb
  const materialCopy = ('useSpecularCalculation' in material) ? {...material} : {
    ...material,
    useSpecularCalculation: SPECULATION_STATUS.ON
  }

  const keys = [
    'AO',
    'diffuse',
    'metallic',
    'normal',
    'roughness',
    'transparency',
    'loadedMaterial',
    'displacement'
  ];
  await Promise.all(
    keys.map(async (key) => {
      let id = material[`${key}Id`];
      if (!id && id !== 'MISSING') return;

      const isNumber = /^\d+$/.test(id);

      if (!isNumber) return;

      id = parseInt(id);

      if (key === 'loadedMaterial') {
        let dbMaterial;

        try {
          dbMaterial = await getMaterial(id);
        } catch (e) {
          console.error(e);
        }

        const filePath = dbMaterial?.file || 'MISSING';
        materialCopy[`${key}Path`] = filePath;
        materialCopy.loadedMatName = dbMaterial?.name;

        if (typeof filePath === 'string' && filePath.toUpperCase() === 'MISSING') {
          // add material to list
          // TODO: handle this case
          const name = material.loadedMatName;
          missingMaterials.push({ id, oldId: materialCopy.oldLoadedMaterialId, name });
          // debugger;
        }
      } else {
        let dbTexture;
        try {
          dbTexture = await getTexture(id);
        } catch (e) {
          console.error(e);
        }
        const filePath = dbTexture?.file || 'MISSING';
        materialCopy[`${key}Path`] = filePath;

        if (typeof filePath === 'string' && filePath.toUpperCase() === 'MISSING') {
          let nameKeyPrefix = key === 'AO' ? 'ao' : key;
          const nameKey = nameKeyPrefix === 'diffuse' ? 'diffuseName' : `${nameKeyPrefix}MapName`;
          const name = materialCopy[nameKey];
          missingTextures.push({ id, name });
        }
      }
    })
  );

  if(groupPropId) {
    const loadingProgress = paginatedProductSelectors.selectItemProgress(getAppState(), groupPropId);

    dispatcher(
      paginatedProductActions.setProgress({
        ...loadingProgress,
        id: groupPropId,
        loaded: loadingProgress?.loaded + 1,
      })
    );

  }

  if (materialCopy.materialTextureId) {
    const dbMaterialTexture = await getMaterialTexture(materialCopy.materialTextureId);
    materialCopy.materialTextureId = dbMaterialTexture?.id || 'MISSING';
    materialCopy.materialTexturePath = dbMaterialTexture?.file || 'MISSING';
  }

  if (!materialCopy.initialMetalIntensity) {
    materialCopy.initialMetalIntensity = 0;
  }

  if (!materialCopy.metalIntensity) {
    materialCopy.metalIntensity = 0;
  }

  delete materialCopy.oldLoadedMaterialId;

  if (Array.isArray(materialCopy.tilingOffsetValues)) {
    materialCopy.tilingOffsetValues = materialCopy.tilingOffsetValues.map((value) => {
      const isTilingXValid = !Number.isNaN(parseFloat(value.tilingX));
      const isTilingYValid = !Number.isNaN(parseFloat(value.tilingY));
      if (!isTilingXValid || !isTilingYValid) {
        const tilingX = isTilingXValid ? value.tilingX : 12;
        const tilingY = isTilingYValid ? value.tilingY : 12;

        return {
          ...value,
          tilingX,
          tilingY,
        };
      }

      return value;
    });
  }

  loadMaterialToUnity?.(materialCopy);
  // debugger;

  return materialCopy;
};
